import React, { useEffect, useRef } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useField, useFormState } from 'react-final-form';
import { getDeterminantNetworkId, networkIdExists } from 'src/components/Referrals/utils/form';
import { ServiceTypeSelectField } from '@unite-us/client-utils';
import { shouldRefetchServiceTypes } from './utils';
import './ReferralServiceTypeSelect.scss';

// Build a fields adapter object matching the { value: ... } shape
// that utility functions (getDeterminantNetworkId, shouldRefetchServiceTypes, etc.) expect.
const buildFieldsAdapter = (values) => ({
  referred_by_network: { value: values.referred_by_network },
  refer_to_outside_network: { value: values.refer_to_outside_network },
  referred_to_network: { value: values.referred_to_network },
  service_type: { value: values.service_type },
});

const ReferralServiceTypeSelect = ({
  className,
  id,
  canSearch,
  setCanSearch,
  updateServiceTypes,
  ...props
}) => {
  const { input } = useField('service_type');
  const { values } = useFormState({ subscription: { values: true } });
  const fields = buildFieldsAdapter(values);
  const prevFieldsRef = useRef(null);

  // componentDidMount: fetch service types if network exists
  useEffect(() => {
    const networkId = getDeterminantNetworkId(fields);
    if (!_.isEmpty(networkId)) {
      updateServiceTypes();
    }
  }, []);

  // UNSAFE_componentWillReceiveProps equivalent: detect field changes
  useEffect(() => {
    if (prevFieldsRef.current === null) {
      prevFieldsRef.current = fields;
      return;
    }

    const prevProps = { fields: prevFieldsRef.current };
    const nextProps = { fields };

    if (!networkIdExists(fields) && canSearch) {
      setCanSearch(false);
    }

    if (shouldRefetchServiceTypes(prevProps, nextProps)) {
      updateServiceTypes();
    }

    prevFieldsRef.current = fields;
  });

  const selectFieldClass = classNames({
    'referral-service-type-select': true,
    [className]: !!className,
  });

  const selectFieldId = classNames({
    'service-type': true,
    [id]: !!id,
  });

  return (
    <ServiceTypeSelectField
      className={selectFieldClass}
      field={input}
      id={selectFieldId}
      registerField={() => {}}
      touch={() => input.onBlur()}
      {...props}
    />
  );
};

ReferralServiceTypeSelect.propTypes = {
  className: PropTypes.string,
  disabled: PropTypes.bool.isRequired,
  id: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.array,
  setCanSearch: PropTypes.func.isRequired,
  updateServiceTypes: PropTypes.func.isRequired,
  validations: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array,
  ]).isRequired,
  canSearch: PropTypes.bool.isRequired,
};

ReferralServiceTypeSelect.defaultProps = {
  className: '',
  forceObjectValue: true,
  id: '',
  options: [],
  placeholder: 'Choose one...',
  required: true,
  searchChoices: false,
  searchPlaceholderValue: 'Search for the service your client seeks',
};

export default ReferralServiceTypeSelect;
